import { api, bytesText, download, listPages, paged, sendFile } from '../console/api.ts';
import { carrierCell } from '../console/carriers.ts';
import {
  empty,
  field,
  fileField,
  loadMore,
  pageHead,
  pill,
  quoteRef,
} from '../console/components.ts';
import { InputError } from '../console/errors.ts';
import { cap, date, esc, fmt, icon, type Money, PRODUCT, person, when } from '../console/format.ts';
import { type Actions, type ClickAction, render } from '../console/router.ts';
import { canQuote, followsClaims, has, supervises } from '../console/session.ts';
import { $, act, textInput } from '../console/ui.ts';
import {
  checkboxControl,
  dateControl,
  numberControl,
  selectControl,
  textareaControl,
  textControl,
} from '../controls.ts';
import { CHANNEL, OUTCOME } from './cobranza.ts';
import { type QuoteSummary, setQuoting } from './cotizar.ts';
import { LEAD_SOURCE, LEAD_STAGE, leadPill } from './prospectos.ts';
import { subjectTasksHtml, type TaskRow } from './seguimiento.ts';
import { type ClaimRow, claimsTable } from './siniestros.ts';

/** «Clientes»: the agency's portfolio (cartera) and each customer's file (ficha) — policies, receipts, quotes, consent and erasure. */

type ConsentState = { channel: string; granted: boolean | null; at: string | null };
type PartyRow = {
  id: string;
  kind: 'PERSON' | 'COMPANY';
  name: string;
  nationalId: string | null;
  nationalIdMasked: boolean;
  taxId: string | null;
  birthDate: string | null;
  email: string | null;
  phone: string | null;
  address: { line: string; department: string | null; kind?: string | null } | null;
  contactPerson: string | null;
  whatsapp?: string | null;
  /** The IVE profile of a person (its sensitive part is null for who may not read it). */
  person?: PersonProfile | null;
  company?: CompanyProfile | null;
  /** The sensitive part of the profile was left out for this role. */
  sensitiveHidden?: boolean;
  ownerAgentId: string;
  /** ANONYMIZED: its data was erased at its request (Habeas Data). */
  status?: 'ACTIVE' | 'MERGED' | 'ANONYMIZED';
  /** prospecto, vigente or de baja. */
  standing?: 'PROSPECT' | 'CURRENT' | 'CLOSED';
  closure?: { at: string; by: string; reason: string; note: string | null } | null;
  erasure?: PartyErasure | null;
  createdAt: string;
  updatedAt: string;
  consent: ConsentState[];
};
type PersonProfile = {
  sex: string | null;
  maritalStatus: string | null;
  occupation: string | null;
  workplace: string | null;
  nationality: string | null;
  politicallyExposed: boolean | null;
  pepPosition: string | null;
  fundsSource: string | null;
  monthlyIncome: string | null;
};
type CompanyProfile = {
  tradeName: string | null;
  economicActivity: string | null;
  incorporatedOn: string | null;
  legalRepresentative: { name: string; nationalId: string | null; position: string | null } | null;
  employees: number | null;
};
/** The customer's request to erase their data: recorded, approved by the direction, done. */
type PartyErasure = {
  requested: { at: string; by: string; note: string | null };
  approved: { at: string; by: string } | null;
  done: { at: string; by: string; reference: string } | null;
};
type PartyInstallment = { number: number; dueDate: string; amount: Money; status: string };
type PartyPolicy = {
  scheduleId: string;
  carrier: string;
  policyNumber: string;
  productLine: string;
  periodStart: string;
  agentId: string;
  status: string;
  installments: number;
  paid: number;
  nextInstallment: PartyInstallment | null;
  overdue: PartyInstallment[];
};
type PartyPage = {
  party: PartyRow;
  mergedFrom?: string;
  absorbed: { id: string; name: string; at: string }[];
  consents: { channel: string; granted: boolean; at: string; by: string; note: string | null }[];
  policies: PartyPolicy[];
  receivables: {
    overdue: (PartyInstallment & { policyNumber: string; scheduleId: string })[];
    totals: Money[];
  };
  quotes: QuoteSummary[];
  leads: {
    id: string;
    name: string;
    stage: string;
    productLine: string;
    source: string;
    agentId: string;
    createdAt: string;
    closedAt: string | null;
  }[];
  workOrders: {
    id: string;
    carrier: string;
    kind: string;
    title: string;
    stage: string;
    agentId: string | null;
    createdAt: string;
    dueAt: string;
  }[];
  underwriting: {
    available: boolean;
    files: {
      id: string;
      quoteId: string;
      carrier: string;
      option: string;
      productLine: string;
      label: string;
      stage: string;
      decision: string;
      complete: boolean;
      readyToIssue: boolean;
    }[];
  };
  /** Only management: policies other agents issued to this customer. */
  notes?: { at: string; agentId: string; policyNumber: string; note: string }[];
  /** Its claims (read apart; null: the person does not follow claims, #309). */
  claims?: ClaimRow[] | null;
  indicators: {
    policiesInForce: number;
    annualPremium: Money[];
    pending: Money[];
    overdueReceipts: number;
    openClaims: number | null;
    /** Management only (null for the rest). */
    commission12m: Money[] | null;
    customerSince: string;
  };
  addresses: { id: string; kind: string; line: string; department: string | null }[];
  contacts: { id: string; kind: string; value: string; name: string | null }[];
  relationships: {
    id: string;
    kind: string;
    role: string;
    other: { id: string | null; name: string | null; kind: string };
    at: string;
  }[];
  customerNotes: {
    id: string;
    body: string;
    pinned: boolean;
    at: string;
    by: string;
    /** Its author's name in the team (#317); null when the team has none. */
    byName?: string | null;
    editedAt: string | null;
    editable: boolean;
  }[];
  /** Its open tasks and those of its policies, quotes and claims (#317). */
  openTasks?: TaskRow[];
  /** Null for who may not read its documents (sensitive). */
  documents:
    | { id: string; kind: string; fileName: string; size: number; at: string; by: string }[]
    | null;
  communications: {
    id: string;
    template: string;
    title: string;
    channel: string;
    status: string;
    simulated: boolean;
    at: string;
  }[];
  /** Who read its sensitive data or downloaded its documents (management only). */
  accessLog?: { at: string; by: string; action: string; item: string }[];
  timeline: { at: string; type: string; ref: string | null; text: string | null; by?: string }[];
  can: {
    update: boolean;
    readSensitive?: boolean;
    merge: boolean;
    quote: boolean;
    requestErasure?: boolean;
    approveErasure?: boolean;
  };
};

const CONTACT_CHANNEL: Record<string, string> = {
  EMAIL: 'Correo',
  WHATSAPP: 'WhatsApp',
  SMS: 'SMS',
  PHONE: 'Llamadas',
};
const PARTY_KIND: Record<string, string> = { PERSON: 'Persona', COMPANY: 'Empresa' };
const DEPARTMENT: Record<string, string> = {
  GUATEMALA: 'Guatemala',
  EL_PROGRESO: 'El Progreso',
  SACATEPEQUEZ: 'Sacatepéquez',
  CHIMALTENANGO: 'Chimaltenango',
  ESCUINTLA: 'Escuintla',
  SANTA_ROSA: 'Santa Rosa',
  SOLOLA: 'Sololá',
  TOTONICAPAN: 'Totonicapán',
  QUETZALTENANGO: 'Quetzaltenango',
  SUCHITEPEQUEZ: 'Suchitepéquez',
  RETALHULEU: 'Retalhuleu',
  SAN_MARCOS: 'San Marcos',
  HUEHUETENANGO: 'Huehuetenango',
  QUICHE: 'Quiché',
  BAJA_VERAPAZ: 'Baja Verapaz',
  ALTA_VERAPAZ: 'Alta Verapaz',
  PETEN: 'Petén',
  IZABAL: 'Izabal',
  ZACAPA: 'Zacapa',
  CHIQUIMULA: 'Chiquimula',
  JALAPA: 'Jalapa',
  JUTIAPA: 'Jutiapa',
};
/** How a policy period stands for the customer (the API derives it from its receipts). */
const PERIOD_STATUS: Record<string, [label: string, pill: string]> = {
  DELINQUENT: ['En mora', 'bad'],
  OVERDUE: ['Recibo vencido', 'warn'],
  UNCONFIRMED: ['Sin confirmar con la aseguradora', 'warn'],
  CURRENT: ['Al día', 'ok'],
  PAID: ['Pagada', 'ok'],
  CANCELLED: ['Cancelada', 'CANCELLED'],
};
const DESK_KIND: Record<string, string> = {
  QUOTE: 'Cotización',
  ISSUE: 'Emisión',
  ENDORSE: 'Endoso',
  RENEW: 'Renovación',
  CANCEL: 'Cancelación',
  CHANGE: 'Cambio',
  OTHER: 'Otra',
};
const PARTY_TABS: [key: string, label: string][] = [
  ['polizas', 'Pólizas'],
  ['siniestros', 'Siniestros'],
  ['cotizaciones', 'Cotizaciones'],
  ['prospecto', 'Prospecto de origen'],
  ['expedientes', 'Expedientes'],
  ['ordenes', 'Órdenes'],
  ['tareas', 'Tareas'],
  ['notas', 'Notas'],
  ['documentos', 'Documentos'],
  ['comunicaciones', 'Comunicaciones'],
  ['historia', 'Historia'],
];

/** The customer's status: prospecto (no policy in force), vigente, de baja. */
const STANDING: Record<string, [label: string, pill: string]> = {
  PROSPECT: ['Prospecto', 'info'],
  CURRENT: ['Vigente', 'ok'],
  CLOSED: ['De baja', 'CANCELLED'],
};
const SEX: Record<string, string> = { FEMALE: 'Femenino', MALE: 'Masculino' };
const MARITAL: Record<string, string> = {
  SINGLE: 'Soltero/a',
  MARRIED: 'Casado/a',
  DOMESTIC_PARTNER: 'Unión de hecho',
  DIVORCED: 'Divorciado/a',
  WIDOWED: 'Viudo/a',
};
/** Where the money comes from (formulario IVE). */
const FUNDS: Record<string, string> = {
  SALARY: 'Salario',
  OWN_BUSINESS: 'Negocio propio',
  REMITTANCES: 'Remesas',
  RENTS: 'Rentas',
  PENSION: 'Pensión o jubilación',
  OTHER: 'Otro',
};
const INCOME: Record<string, string> = {
  UNDER_5K: 'Menos de Q5,000',
  FROM_5K_TO_10K: 'Q5,000 a Q10,000',
  FROM_10K_TO_25K: 'Q10,000 a Q25,000',
  FROM_25K_TO_50K: 'Q25,000 a Q50,000',
  OVER_50K: 'Más de Q50,000',
};
const ADDRESS_KIND: Record<string, string> = {
  HOME: 'Domicilio',
  BILLING: 'Cobro',
  WORK: 'Trabajo',
  OTHER: 'Otra',
};
const CONTACT_KIND: Record<string, string> = {
  PHONE: 'Teléfono',
  EMAIL: 'Correo',
  WHATSAPP: 'WhatsApp',
};
/** «Este cliente es … de»: what is recorded. */
const RELATIONSHIP: Record<string, string> = {
  SPOUSE: 'Cónyuge de',
  CHILD: 'Hijo/a de',
  PARENT: 'Padre o madre de',
  SIBLING: 'Hermano/a de',
  OTHER_RELATIVE: 'Otro familiar de',
  EMPLOYEE_OF: 'Empleado/a de',
  LEGAL_REPRESENTATIVE_OF: 'Representante legal de',
  PARTNER_OF: 'Socio/a de',
};
/** What the other customer is to the one whose record is open. */
const ROLE: Record<string, string> = {
  SPOUSE: 'Cónyuge',
  CHILD: 'Hijo/a',
  PARENT: 'Padre o madre',
  SIBLING: 'Hermano/a',
  OTHER_RELATIVE: 'Otro familiar',
  EMPLOYEE: 'Empleado/a',
  EMPLOYER: 'Empleador',
  LEGAL_REPRESENTATIVE: 'Representante legal',
  REPRESENTED_COMPANY: 'Empresa que representa',
  PARTNER: 'Socio/a',
  PARTNER_COMPANY: 'Empresa de la que es socio/a',
};
const CLOSURE_REASON: Record<string, string> = {
  CUSTOMER_REQUEST: 'Solicitud del cliente',
  DECEASED: 'Fallecimiento',
  DUPLICATE: 'Registro duplicado',
  INACTIVE: 'Sin actividad',
  OTHER: 'Otro motivo',
};
const DOCUMENT_KIND: Record<string, string> = {
  NATIONAL_ID: 'DPI',
  TAX_REGISTRATION: 'RTU',
  PROOF_OF_ADDRESS: 'Comprobante de domicilio',
  PROOF_OF_INCOME: 'Comprobante de ingresos',
  OTHER: 'Otro documento',
};
/** What each message to the customer was (the template that wrote it). */
const MESSAGE: Record<string, string> = {
  'reminders.installment_upcoming': 'Recordatorio de pago: el recibo vence pronto',
  'reminders.installment_due': 'Recordatorio de pago: el recibo vence hoy',
  'reminders.installment_overdue': 'Aviso de recibo vencido',
  'reminders.renewal_policyholder': 'Aviso de renovación',
  'billing.payment_link': 'Enlace de pago',
  'quotes.proposal': 'Propuesta de cotización',
};
const CHANNEL_NAME: Record<string, string> = { EMAIL: 'Correo', WHATSAPP: 'WhatsApp', SMS: 'SMS' };

let partyQuery = '';
let openPartyId: string | null = null;
let partyTab = 'polizas';
/** The open customer's data is being corrected (the «Datos» card is a form). */
let editingParty = false;
/** The note its author is correcting (within 24 hours). */
let editingNote: string | null = null;
/** The portfolio's filters: its status and, for management, one agent. */
let partyStanding = '';
let partyAgent = '';
/** A new session starts on the portfolio, with no customer open and no search. */
export function resetParties() {
  partyQuery = '';
  openPartyId = null;
  partyTab = 'polizas';
  editingParty = false;
  partyStanding = '';
  partyAgent = '';
}
/** «Clientes» in the menu is the list, even when a customer's page was open. */
export function closeParty() {
  openPartyId = null;
  editingParty = false;
}

/** "2451839200101" → "2451 83920 0101"; a masked DPI is shown as the API sends it. */
const dpiText = (v: string | null) =>
  v && /^\d{13}$/.test(v) ? `${v.slice(0, 4)} ${v.slice(4, 9)} ${v.slice(9)}` : (v ?? '—');
const phoneText = (v: string | null) =>
  v && /^\+502\d{8}$/.test(v) ? `+502 ${v.slice(4, 8)} ${v.slice(8)}` : (v ?? '');
const consentPill = (c: ConsentState) =>
  c.granted === null
    ? pill('DRAFT', `${CONTACT_CHANNEL[c.channel] ?? c.channel}: sin registro`)
    : pill(
        c.granted ? 'ok' : 'bad',
        `${CONTACT_CHANNEL[c.channel] ?? c.channel}: ${c.granted ? 'autoriza' : 'no autoriza'}`,
      );
const periodPill = (s: string) => {
  const [label, cls] = PERIOD_STATUS[s] ?? [s, 'info'];
  return pill(cls, label);
};
const standingPill = (s: string | undefined) => {
  if (!s) return '';
  const [label, cls] = STANDING[s] ?? [s, 'info'];
  return pill(cls, label);
};
const options = (labels: Record<string, string>, empty?: string) => [
  ...(empty === undefined ? [] : [{ value: '', label: empty }]),
  ...Object.entries(labels).map(([value, label]) => ({ value, label })),
];
const chosen = (labels: Record<string, string>, value: string | null | undefined, empty: string) =>
  options(labels, empty).map((o) => ({ ...o, selected: o.value === (value ?? '') }));
/** Several amounts (one per currency), or a dash. */
const amounts = (ms: Money[] | null | undefined) => (ms?.length ? ms.map(fmt).join(' · ') : '—');

/** The register form: a person (DPI) or a company (NIT), with contact and address. */
function partyForm(manage: boolean, agents: string[]) {
  return `<details class="card party-new"${partyQuery ? '' : ' open'}><summary class="card-head"><h2>Registrar un cliente</h2><span class="spacer"></span><span class="muted small">Persona con DPI o empresa con NIT</span></summary>
    <form id="party-form" novalidate>
    <div class="form-grid">
      <fieldset><legend>Quién es</legend>
        ${field(
          'Tipo',
          selectControl({ id: 'p-kind' }, [
            { value: 'PERSON', label: 'Persona' },
            { value: 'COMPANY', label: 'Empresa' },
          ]),
        )}
        ${field('Nombre completo o razón social', textControl({ id: 'p-name', maxlength: 160, autocomplete: 'off', placeholder: 'Pedro Antonio Gómez Chen' }))}
        ${field('DPI (CUI)', textControl({ id: 'p-cui', inputmode: 'numeric', maxlength: 15, autocomplete: 'off', placeholder: '13 dígitos; solo personas' }))}
        ${field('NIT', textControl({ id: 'p-nit', maxlength: 15, autocomplete: 'off', placeholder: '1234567-8' }))}
        ${field('Fecha de nacimiento', dateControl({ id: 'p-birth' }))}
      </fieldset>
      <fieldset><legend>Contacto</legend>
        ${field('Correo', textControl({ id: 'p-email', type: 'email', maxlength: 160, autocomplete: 'off', placeholder: 'pedro@correo.com' }))}
        ${field('Teléfono', textControl({ id: 'p-phone', inputmode: 'tel', maxlength: 40, autocomplete: 'off', placeholder: '+502 5555 0199' }))}
        ${field('Persona de contacto (empresas)', textControl({ id: 'p-contact', maxlength: 160, autocomplete: 'off', placeholder: 'Marta Ajú' }))}
      </fieldset>
      <fieldset><legend>Dirección</legend>
        ${field('Dirección', textControl({ id: 'p-address', maxlength: 300, autocomplete: 'off', placeholder: '4a. avenida 12-45, zona 1' }))}
        ${field(
          'Departamento',
          selectControl({ id: 'p-department' }, [
            { value: '', label: 'Sin indicar' },
            ...Object.entries(DEPARTMENT).map(([k, v]) => ({ value: k, label: v })),
          ]),
        )}
        ${
          manage
            ? field(
                'Agente a cargo',
                selectControl(
                  { id: 'p-agent' },
                  agents.map((a) => ({ value: a, label: cap(a) })),
                ),
              )
            : ''
        }
      </fieldset>
    </div>
    <div class="form-foot"><span class="muted small">Cada DPI (o NIT de empresa) es un solo cliente en la agencia. Al emitir, el contratante se registra solo.</span>
      <button class="btn primary" type="submit" id="party-submit">Registrar cliente</button></div></form></details>`;
}

/** The portfolio's query: its search and filters, as the list and the export ask them. */
const portfolioQuery = () =>
  new URLSearchParams({
    ...(partyQuery ? { q: partyQuery } : {}),
    ...(partyStanding ? { status: partyStanding } : {}),
    ...(partyAgent ? { agent: partyAgent } : {}),
  }).toString();

export async function clientes() {
  if (openPartyId) return clientePage(openPartyId);
  const manage = supervises();
  // Management (and the agency manager) export the portfolio.
  const exports = manage || has('agency_manager');
  const query = portfolioQuery();
  const [partyPages, agents] = await Promise.all([
    paged<PartyRow>('clientes', `/v1/parties${query ? `?${query}` : ''}`),
    manage
      ? api<{ agentId: string }[]>('GET', '/v1/agents').then((a) => a.map((x) => x.agentId))
      : Promise.resolve([] as string[]),
  ]);
  let html = pageHead(
    'Clientes',
    manage
      ? 'La cartera de toda la agencia: cada cliente con sus pólizas, recibos, cotizaciones y lo que autorizó.'
      : 'Tus clientes: sus pólizas y recibos, lo que les cotizaste y por dónde autorizan que los contactes.',
  );
  const filtered = !!(partyQuery || partyStanding || partyAgent);
  html += `<form id="party-search-form" class="card toolbar party-search" role="search">
    <div class="field grow">${textControl({ label: 'Buscar por nombre, DPI, NIT, teléfono o correo', id: 'party-q', type: 'search', maxlength: 120, autocomplete: 'off', value: partyQuery, placeholder: 'Gómez · 0101 · 1234567-8 · 5555 1234' })}</div>
    ${field('Estado', selectControl({ id: 'party-standing' }, chosen(Object.fromEntries(Object.entries(STANDING).map(([k, [l]]) => [k, l])), partyStanding, 'Todos')))}
    ${manage ? field('Agente', selectControl({ id: 'party-agent' }, chosen(Object.fromEntries(agents.map((a) => [a, cap(a)])), partyAgent, 'Todos'))) : ''}
    <div class="order-buttons"><button class="btn primary" type="submit" id="party-search">Buscar</button>${filtered ? '<button type="button" class="btn" data-party-clear>Ver todos</button>' : ''}${exports ? `<button type="button" class="btn" data-party-export>${icon('download')}Exportar a Excel</button>` : ''}</div></form>`;
  html += partyForm(manage, agents);
  const parties = partyPages.items;
  if (!parties.length)
    return (
      html +
      (filtered
        ? empty(
            'users',
            'Ningún cliente coincide',
            partyQuery
              ? `No hay clientes con «${esc(partyQuery)}». Prueba con el apellido, los últimos 4 dígitos del DPI o el teléfono.`
              : 'Ningún cliente tiene ese estado o agente. Cambia el filtro o vuelve a ver todos.',
          )
        : empty(
            'users',
            'Todavía no hay clientes',
            'Registra el primero con el formulario de arriba o emite una póliza: su contratante queda como cliente.',
          ))
    );
  const rows = parties
    .map(
      (
        p,
      ) => `<tr><td><strong>${esc(p.name)}</strong><div class="muted small">${esc(PARTY_KIND[p.kind] ?? p.kind)}${p.contactPerson ? ` · contacto ${esc(p.contactPerson)}` : ''}</div></td>
        <td class="mono nowrap">${p.kind === 'PERSON' ? `DPI ${esc(dpiText(p.nationalId))}` : `NIT ${esc(p.taxId ?? '')}`}</td>
        <td>${esc(phoneText(p.phone))}${p.email ? `<div class="muted small">${esc(p.email)}</div>` : ''}</td>
        ${manage ? `<td>${esc(cap(p.ownerAgentId))}</td>` : ''}
        <td>${standingPill(p.standing)}${p.person?.politicallyExposed ? ` ${pill('warn', 'PEP')}` : ''}</td>
        <td><div class="consent-pills">${p.consent
          .filter((c) => c.granted !== null)
          .map(consentPill)
          .join(
            '',
          )}${p.consent.every((c) => c.granted === null) ? '<span class="muted small">Sin consentimiento registrado</span>' : ''}</div></td>
        <td class="actions"><button type="button" class="btn sm" data-party-open="${esc(p.id)}">Abrir</button></td></tr>`,
    )
    .join('');
  html += `<section class="card"><div class="card-head"><h2>${partyQuery ? `Resultados para «${esc(partyQuery)}»` : filtered ? 'Cartera filtrada' : 'Cartera'}</h2><span class="spacer"></span><span class="muted small">${parties.length} ${parties.length === 1 ? 'cliente' : 'clientes'}${partyPages.more ? ' por ahora' : ''}</span></div>
    <div class="table-wrap" data-component="table"><table class="table"><thead><tr><th>Cliente</th><th>Identificación</th><th>Contacto</th>${manage ? '<th>Agente</th>' : ''}<th>Estado</th><th>Consentimiento</th><th class="actions"><span class="sr-only">Acción</span></th></tr></thead>
    <tbody>${rows}</tbody></table></div>${loadMore('clientes', partyPages.more, parties.length, 'clientes')}</section>`;
  return html;
}

function partyTabBody(v: PartyPage) {
  if (partyTab === 'siniestros')
    return v.claims?.length
      ? claimsTable(v.claims, { caption: `Siniestros de ${v.party.name}` })
      : `<p class="muted party-none">Este cliente no tiene siniestros avisados. Se avisan desde la póliza, en su pestaña «Siniestros».</p>`;
  if (partyTab === 'cotizaciones')
    return v.quotes.length
      ? `<div class="table-wrap" data-component="table"><table class="table"><thead><tr><th>Riesgo</th><th class="num">Versiones</th><th class="num">Mejor prima</th><th>Estado</th><th>Actualizada</th><th class="actions"><span class="sr-only">Acción</span></th></tr></thead><tbody>
        ${v.quotes
          .map(
            (
              q,
            ) => `<tr><td><strong>${esc(q.label)}</strong><div class="muted small">${quoteRef(q)}</div></td>
          <td class="num">${q.versions}</td><td class="num">${fmt(q.bestPremium)}</td>
          <td>${q.binding ? pill('ok', `Emitida · ${esc(q.binding.policyNumber)}`) : pill('info', 'Abierta')}</td>
          <td class="nowrap">${esc(when(q.updatedAt))}</td>
          <td class="actions"><button type="button" class="btn sm" data-open-quote="${esc(q.id)}">Abrir</button></td></tr>`,
          )
          .join('')}</tbody></table></div>`
      : `<p class="muted party-none">Todavía no hay cotizaciones para este cliente.</p>`;
  if (partyTab === 'prospecto')
    return v.leads.length
      ? `<ul class="party-list">${v.leads
          .map(
            (l) => `<li><div><strong>${esc(l.name)}</strong> ${leadPill(l.stage)}
            <div class="muted small">${esc(PRODUCT[l.productLine] ?? l.productLine)} · ${esc(LEAD_SOURCE[l.source] ?? l.source)} · captado el ${esc(date(l.createdAt.slice(0, 10)))} por ${esc(cap(l.agentId))}</div></div>
            <button type="button" class="btn sm" data-go-lead="${esc(l.id)}">Ver prospecto</button></li>`,
          )
          .join('')}</ul>`
      : `<p class="muted party-none">No vino de un prospecto registrado en Nubi.</p>`;
  if (partyTab === 'expedientes')
    return v.underwriting.files.length
      ? `<div class="table-wrap" data-component="table"><table class="table"><thead><tr><th>Expediente</th><th>Aseguradora</th><th>Opción</th><th>Estado</th><th class="actions"><span class="sr-only">Acción</span></th></tr></thead><tbody>
        ${v.underwriting.files
          .map(
            (
              f,
            ) => `<tr><td><strong>${esc(f.label)}</strong><div class="muted small mono">${esc(f.id)}</div></td>
            <td>${carrierCell(f.carrier)}</td><td>${esc(f.option)} · ${esc(PRODUCT[f.productLine] ?? f.productLine)}</td>
            <td>${f.readyToIssue ? pill('ok', 'Listo para emitir') : f.complete ? pill('info', 'Completo') : pill('warn', 'Incompleto')}</td>
            <td class="actions"><button type="button" class="btn sm" data-open-quote="${esc(f.quoteId)}">Ver cotización</button></td></tr>`,
          )
          .join('')}</tbody></table></div>
        <p class="muted small">${icon('lock')} Las declaraciones de salud y los documentos se abren desde la cotización; cada descarga queda registrada.</p>`
      : `<p class="muted party-none">No hay expedientes de suscripción para este cliente.</p>`;
  if (partyTab === 'ordenes')
    return v.workOrders.length
      ? `<div class="table-wrap" data-component="table"><table class="table"><thead><tr><th>Orden</th><th>Aseguradora</th><th>Tipo</th><th>Estado</th><th>Vence</th></tr></thead><tbody>
        ${v.workOrders
          .map(
            (
              o,
            ) => `<tr><td><strong>${esc(o.title)}</strong><div class="muted small mono">${esc(o.id)}</div></td>
            <td>${carrierCell(o.carrier)}</td><td>${esc(DESK_KIND[o.kind] ?? o.kind)}</td><td>${pill(o.stage)}</td>
            <td class="nowrap">${esc(when(o.dueAt))}</td></tr>`,
          )
          .join('')}</tbody></table></div>`
      : `<p class="muted party-none">No hay órdenes de trabajo para este cliente.</p>`;
  if (partyTab === 'tareas')
    return subjectTasksHtml(v.openTasks ?? [], { kind: 'PARTY', id: v.party.id });
  if (partyTab === 'notas') return notesTab(v);
  if (partyTab === 'documentos') return documentsTab(v);
  if (partyTab === 'comunicaciones')
    return v.communications.length
      ? `<div class="table-wrap" data-component="table"><table class="table"><thead><tr><th>Qué se le envió</th><th>Canal</th><th>Fecha</th><th>Entrega</th></tr></thead><tbody>
        ${v.communications
          .map(
            (
              n,
            ) => `<tr><td><strong>${esc(MESSAGE[n.template] ?? n.title)}</strong><div class="muted small">${esc(n.title)}</div></td>
            <td>${esc(CHANNEL_NAME[n.channel] ?? n.channel)}</td><td class="nowrap">${esc(when(n.at))}</td>
            <td>${n.simulated ? pill('warn', 'Simulado: no salió de Nubi') : n.status === 'SENT' ? pill('ok', 'Entregado al canal') : pill('bad', 'No se entregó')}</td></tr>`,
          )
          .join('')}</tbody></table></div>
        <p class="muted small party-consent-note">Avisos, recordatorios y enlaces de pago que Nubi le envió por los canales que autorizó.</p>`
      : `<p class="muted party-none">Nubi todavía no le ha enviado avisos, recordatorios ni enlaces de pago.</p>`;
  /** "CALL:PROMISED:2026-12-05" → «Póliza X · Llamada · Promesa de pago para el 5 dic 2026». */
  const effortNote = (ref: string | null, text: string | null) => {
    const [channel = '', outcome = '', promised = ''] = (text ?? '').split(':');
    return `Póliza <b class="mono">${esc(ref)}</b> · ${esc(CHANNEL[channel] ?? channel)} · ${esc(OUTCOME[outcome] ?? outcome)}${promised ? ` para el ${esc(date(promised))}` : ''}`;
  };
  if (partyTab === 'historia') {
    const label: Record<string, [string, string]> = {
      REGISTERED: ['Registrado', 'info'],
      UPDATED: ['Datos', 'info'],
      POLICYHOLDER: ['Contratante', 'ok'],
      CONSENT: ['Consentimiento', 'warn'],
      MERGED: ['Fusión', 'warn'],
      ERASURE_REQUESTED: ['Pidió borrar sus datos', 'warn'],
      ERASURE_APPROVED: ['Borrado aprobado', 'warn'],
      ERASURE_DONE: ['Datos anonimizados', 'bad'],
      CLOSED: ['Baja', 'bad'],
      REOPENED: ['Reactivado', 'ok'],
      QUOTE: ['Cotización', 'info'],
      POLICY_PERIOD: ['Vigencia', 'ok'],
      LEAD: ['Prospecto', 'DRAFT'],
      WORK_ORDER: ['Orden', 'info'],
      COLLECTION_EFFORT: ['Gestión de cobro', 'warn'],
    };
    return `<ol class="timeline">${v.timeline
      .map((e) => {
        const [text, cls] = label[e.type] ?? [e.type, 'info'];
        const note =
          e.type === 'POLICY_PERIOD'
            ? `Póliza <b class="mono">${esc(e.ref)}</b> · ${esc(PRODUCT[e.text ?? ''] ?? e.text)}`
            : e.type === 'LEAD'
              ? `Prospecto <b class="mono">${esc(e.ref)}</b> · ${esc(LEAD_STAGE[e.text ?? '']?.[0] ?? e.text)}`
              : e.type === 'COLLECTION_EFFORT'
                ? effortNote(e.ref, e.text)
                : `${e.ref ? `<b class="mono">${esc(e.ref)}</b> · ` : ''}${esc(e.text ?? '')}`;
        return `<li>${pill(cls, text)}<span class="muted small">${esc(when(e.at))}${e.by ? ` · ${esc(person(e.by))}` : ''}</span><span class="timeline-note">${note}</span></li>`;
      })
      .join('')}</ol>`;
  }
  return v.policies.length
    ? `<div class="table-wrap" data-component="table"><table class="table"><thead><tr><th>Póliza</th><th>Aseguradora</th><th>Vigencia desde</th><th>Estado</th><th>Próximo recibo</th><th class="num">Pagados</th><th class="actions"><span class="sr-only">Acción</span></th></tr></thead><tbody>
      ${v.policies
        .map(
          (
            p,
          ) => `<tr><td><span class="mono">${esc(p.policyNumber)}</span><div class="muted small">${esc(PRODUCT[p.productLine] ?? p.productLine)} · agente ${esc(cap(p.agentId))}</div></td>
          <td>${carrierCell(p.carrier)}</td><td class="nowrap">${esc(date(p.periodStart))}</td>
          <td>${periodPill(p.status)}${p.overdue.length ? `<div class="muted small late-text">${p.overdue.length} vencido${p.overdue.length === 1 ? '' : 's'}</div>` : ''}</td>
          <td class="nowrap">${p.nextInstallment ? `${fmt(p.nextInstallment.amount)}<div class="muted small">vence ${esc(date(p.nextInstallment.dueDate))}</div>` : '<span class="muted">—</span>'}</td>
          <td class="num">${p.paid} de ${p.installments}</td>
          <td class="actions"><button type="button" class="btn sm" data-go="polizas?poliza=${esc(p.scheduleId)}" aria-label="Ver la póliza ${esc(p.policyNumber)}">Ver póliza</button></td></tr>`,
        )
        .join('')}</tbody></table></div>`
    : `<p class="muted party-none">Todavía no tiene pólizas. Cotízale desde aquí.</p>`;
}

/** Notes on the customer: pinned first; the author corrects one within 24 hours. */
function notesTab(v: PartyPage) {
  const p = v.party;
  const open = v.can.update && p.status === 'ACTIVE';
  const list = v.customerNotes.length
    ? `<ul class="party-list party-notes">${v.customerNotes
        .map((n) =>
          n.id === editingNote && n.editable
            ? `<li><div class="field grow">${textareaControl({ label: 'Corregir la nota', id: 'pn-fix', maxlength: 2000, rows: 2, value: n.body })}</div>
              <div class="order-buttons"><button type="button" class="btn sm" data-note-cancel>Cancelar</button><button type="button" class="btn sm primary" data-note-save="${esc(n.id)}">Guardar corrección</button></div></li>`
            : `<li class="${n.pinned ? 'pinned' : ''}"><div class="grow">${n.pinned ? `${pill('info', 'Fijada')} ` : ''}${n.body ? esc(n.body) : '<span class="muted">Nota borrada a pedido del cliente.</span>'}
          <div class="muted small">${esc(when(n.at))} · ${esc(n.byName ?? person(n.by))}${n.editedAt ? ' · corregida' : ''}</div></div>
          ${
            open && n.body
              ? `<div class="order-buttons">${n.editable ? `<button type="button" class="btn sm" data-note-edit="${esc(n.id)}">Corregir</button>` : ''}<button type="button" class="btn sm" data-note-pin="${esc(n.id)}" data-pinned="${n.pinned ? 'false' : 'true'}">${n.pinned ? 'Desfijar' : 'Fijar'}</button></div>`
              : ''
          }</li>`,
        )
        .join('')}</ul>`
    : '<p class="muted party-none">Todavía no hay notas de este cliente.</p>';
  const form = open
    ? `<div class="toolbar note-form" id="note-form">
      <div class="field grow">${textareaControl({ label: 'Nota', id: 'pn-body', maxlength: 2000, rows: 2, placeholder: 'Prefiere que lo llamen por la tarde' })}</div>
      <div class="field">${checkboxControl({ id: 'pn-pinned', class: 'check' }, 'Fijar arriba')}</div>
      <div class="order-buttons"><button type="button" class="btn" data-note-add="${esc(p.id)}">Agregar nota</button></div></div>
      <p class="muted small party-consent-note">Quien la escribe puede corregirla durante 24 horas; después queda como está y se agrega una nueva.</p>`
    : '';
  return `${list}${form}`;
}

/** Its documents (DPI, RTU, proofs): only who may read its sensitive data; every view is recorded. */
function documentsTab(v: PartyPage) {
  const p = v.party;
  if (v.documents === null)
    return `<p class="muted party-none">${icon('lock')} Los documentos del cliente los ven la agente a cargo, la dirección y la administración.</p>`;
  const list = v.documents.length
    ? `<div class="table-wrap" data-component="table"><table class="table"><thead><tr><th>Documento</th><th>Archivo</th><th>Subido</th><th class="actions"><span class="sr-only">Acción</span></th></tr></thead><tbody>
      ${v.documents
        .map(
          (
            d,
          ) => `<tr><td><strong>${esc(DOCUMENT_KIND[d.kind] ?? d.kind)}</strong></td><td>${esc(d.fileName)}<div class="muted small">${esc(bytesText(d.size))}</div></td>
          <td class="nowrap">${esc(when(d.at))}<div class="muted small">${esc(person(d.by))}</div></td>
          <td class="actions"><button type="button" class="btn sm" data-party-doc="${esc(d.id)}">${icon('download')}Ver</button></td></tr>`,
        )
        .join('')}</tbody></table></div>`
    : '<p class="muted party-none">Todavía no hay documentos de este cliente.</p>';
  const form =
    v.can.update && p.status === 'ACTIVE'
      ? `<div class="toolbar doc-form">
        ${field('Tipo de documento', selectControl({ id: 'pd-kind' }, options(DOCUMENT_KIND)))}
        ${fileField('Archivo', { id: 'pd-file', accept: 'application/pdf,image/jpeg,image/png,image/webp', hint: 'PDF, JPG, PNG o WebP · hasta 5 MB' })}
        <div class="order-buttons"><button type="button" class="btn" data-party-upload="${esc(p.id)}">${icon('upload')}Subir documento</button></div></div>`
      : '';
  const log = v.accessLog
    ? `<details class="access-log"><summary class="muted small">${icon('lock')} Bitácora de accesos (${v.accessLog.length})</summary>
      ${
        v.accessLog.length
          ? `<ol class="timeline">${v.accessLog
              .map(
                (a) =>
                  `<li>${pill('info', a.action === 'DOWNLOAD' ? `Abrió ${DOCUMENT_KIND[a.item] ?? a.item}` : 'Leyó sus datos sensibles')}<span class="muted small">${esc(when(a.at))} · ${esc(person(a.by))}</span></li>`,
              )
              .join('')}</ol>`
          : '<p class="muted party-none">Nadie ha leído aún sus datos sensibles.</p>'
      }</details>`
    : '';
  return `${list}${form}${log}
    <p class="muted small party-consent-note">${icon('lock')} Cada vez que alguien abre un documento o lee sus datos sensibles queda registrado quién y cuándo.</p>`;
}

/** The IVE profile of a person, or the company's data, as rows of the «Datos» card. */
function profileRows(p: PartyRow): [string, string][] {
  const x = p.person;
  const c = p.company;
  if (p.kind === 'COMPANY')
    return [
      ['Nombre comercial', esc(c?.tradeName ?? '')],
      ['Actividad económica', esc(c?.economicActivity ?? '')],
      ['Constitución', c?.incorporatedOn ? esc(date(c.incorporatedOn)) : ''],
      [
        'Representante legal',
        c?.legalRepresentative
          ? `${esc(c.legalRepresentative.name)}${c.legalRepresentative.position ? ` · ${esc(c.legalRepresentative.position)}` : ''}${c.legalRepresentative.nationalId ? `<div class="muted small mono">DPI ${esc(dpiText(c.legalRepresentative.nationalId))}</div>` : ''}`
          : '',
      ],
      ['Empleados', c?.employees === null || c?.employees === undefined ? '' : String(c.employees)],
    ];
  return [
    ['Sexo', esc(SEX[x?.sex ?? ''] ?? '')],
    ['Estado civil', esc(MARITAL[x?.maritalStatus ?? ''] ?? '')],
    ['Ocupación', esc(x?.occupation ?? '')],
    ['Lugar de trabajo', esc(x?.workplace ?? '')],
    ['Nacionalidad', esc(x?.nationality ?? '')],
    [
      'Persona expuesta políticamente (PEP)',
      x?.politicallyExposed === true
        ? `${pill('warn', 'Sí')} ${esc(x.pepPosition ?? '')}`
        : x?.politicallyExposed === false
          ? 'No'
          : '',
    ],
    ['Origen de los fondos', esc(FUNDS[x?.fundsSource ?? ''] ?? '')],
    ['Ingreso mensual', esc(INCOME[x?.monthlyIncome ?? ''] ?? '')],
  ];
}

/**
 * «Datos» as a form: who may register the customer corrects it here; each change goes to its
 * Historia (which fields, who and when). A masked DPI is not shown, so it is not changed; the
 * sensitive part of the profile only to who may read it.
 */
function partyEditForm(v: PartyPage) {
  const p = v.party;
  const x = p.person;
  const c = p.company;
  const person = p.kind === 'PERSON';
  const sensitive = v.can.readSensitive === true;
  const t = (label: string, id: string, value: string | null | undefined, a = {}) =>
    field(
      label,
      textControl({ id, maxlength: 160, autocomplete: 'off', value: value ?? '', ...a }),
    );
  const who = person
    ? `${p.nationalIdMasked ? '' : t('DPI (CUI)', 'pf-cui', p.nationalId, { inputmode: 'numeric', maxlength: 15 })}
       ${t('NIT', 'pf-nit', p.taxId, { maxlength: 15 })}
       ${field('Fecha de nacimiento', dateControl({ id: 'pf-birth', value: p.birthDate ?? '' }))}
       ${field('Sexo', selectControl({ id: 'pf-sex' }, chosen(SEX, x?.sex, 'Sin indicar')))}
       ${field('Estado civil', selectControl({ id: 'pf-marital' }, chosen(MARITAL, x?.maritalStatus, 'Sin indicar')))}
       ${t('Nacionalidad', 'pf-nationality', x?.nationality, { maxlength: 60, placeholder: 'Guatemalteca' })}`
    : `${t('NIT', 'pf-nit', p.taxId, { maxlength: 15 })}
       ${t('Nombre comercial', 'pf-trade', c?.tradeName)}
       ${t('Actividad económica', 'pf-activity', c?.economicActivity, { placeholder: 'Transporte de carga' })}
       ${field('Fecha de constitución', dateControl({ id: 'pf-incorporated', value: c?.incorporatedOn ?? '' }))}
       ${field('Empleados', numberControl({ id: 'pf-employees', min: 0, max: 1000000, value: c?.employees ?? '' }))}`;
  const work = person
    ? `<fieldset><legend>Trabajo e ingresos (formulario IVE)</legend>
        ${t('Ocupación o profesión', 'pf-occupation', x?.occupation, { maxlength: 120 })}
        ${t('Lugar de trabajo', 'pf-workplace', x?.workplace, { maxlength: 120 })}
        ${
          sensitive
            ? `<div class="field">${checkboxControl({ id: 'pf-pep', class: 'check', checked: x?.politicallyExposed === true }, 'Es persona expuesta políticamente (PEP)')}</div>
        ${t('Cargo público (si es PEP)', 'pf-pep-position', x?.pepPosition, { maxlength: 120, placeholder: 'Alcaldesa' })}
        ${field('Origen de los fondos', selectControl({ id: 'pf-funds' }, chosen(FUNDS, x?.fundsSource, 'Sin indicar')))}
        ${field('Ingreso mensual', selectControl({ id: 'pf-income' }, chosen(INCOME, x?.monthlyIncome, 'Sin indicar')))}`
            : `<p class="muted small">${icon('lock')} PEP, origen de los fondos e ingreso los registran la agente a cargo, la dirección y la administración.</p>`
        }</fieldset>`
    : `<fieldset><legend>Representante legal</legend>
        ${t('Nombre', 'pf-rep-name', c?.legalRepresentative?.name)}
        ${t('DPI (CUI)', 'pf-rep-dpi', c?.legalRepresentative?.nationalId, { inputmode: 'numeric', maxlength: 15 })}
        ${t('Cargo', 'pf-rep-position', c?.legalRepresentative?.position, { maxlength: 120 })}
        ${t('Persona de contacto', 'pf-contact', p.contactPerson)}</fieldset>`;
  return `<section class="card party-edit" id="party-data-card"><div class="card-head"><h2>Corregir los datos</h2><span class="spacer"></span><span class="muted small">Cada cambio queda en la Historia: qué cambió, quién y cuándo.</span></div>
    <form id="party-edit-form" novalidate data-party="${esc(p.id)}">
    <div class="form-grid">
      <fieldset><legend>Quién es</legend>
        ${field('Nombre completo o razón social', textControl({ id: 'pf-name', maxlength: 160, autocomplete: 'off', value: p.name, required: true }), { required: true })}
        ${who}</fieldset>
      <fieldset><legend>Contacto principal</legend>
        ${t('Teléfono', 'pf-phone', phoneText(p.phone), { inputmode: 'tel', maxlength: 40 })}
        ${t('WhatsApp', 'pf-whatsapp', phoneText(p.whatsapp ?? null), { inputmode: 'tel', maxlength: 40 })}
        ${t('Correo', 'pf-email', p.email, { type: 'email' })}
        ${t('Dirección', 'pf-address', p.address?.line, { maxlength: 300 })}
        ${field('Departamento', selectControl({ id: 'pf-department' }, chosen(DEPARTMENT, p.address?.department, 'Sin indicar')))}</fieldset>
      ${work}
    </div>
    <div class="form-foot"><button type="button" class="btn" data-party-edit-cancel>Cancelar</button>
      <button class="btn primary" type="submit" id="party-edit-submit">Guardar cambios</button></div></form></section>`;
}

/** Its addresses and contacts: the main one of each kind (in «Datos») and the others. */
function contactsCard(v: PartyPage) {
  const p = v.party;
  const open = v.can.update && p.status === 'ACTIVE';
  const main = [
    p.address?.line
      ? `<li><div><span class="muted small">Dirección principal${p.address.kind ? ` · ${esc(ADDRESS_KIND[p.address.kind] ?? p.address.kind)}` : ''}</span><div>${esc([p.address.line, p.address.department ? DEPARTMENT[p.address.department] : null].filter(Boolean).join(', '))}</div></div></li>`
      : '',
    ...(
      [
        ['PHONE', p.phone],
        ['WHATSAPP', p.whatsapp],
        ['EMAIL', p.email],
      ] as const
    ).map(([k, value]) =>
      value
        ? `<li><div><span class="muted small">${CONTACT_KIND[k]} principal</span><div>${esc(k === 'EMAIL' ? value : phoneText(value))}</div></div></li>`
        : '',
    ),
  ].join('');
  const row = (label: string, text: string, kind: 'address' | 'contact', id: string) =>
    `<li><div><span class="muted small">${label}</span><div>${text}</div></div>${
      open
        ? `<div class="order-buttons"><button type="button" class="btn sm" data-party-main="${kind}:${esc(id)}">Hacer principal</button><button type="button" class="btn sm" data-party-remove="${kind}:${esc(id)}">Quitar</button></div>`
        : ''
    }</li>`;
  const others = [
    ...v.addresses.map((a) =>
      row(
        `Dirección · ${esc(ADDRESS_KIND[a.kind] ?? a.kind)}`,
        esc([a.line, a.department ? DEPARTMENT[a.department] : null].filter(Boolean).join(', ')),
        'address',
        a.id,
      ),
    ),
    ...v.contacts.map((x) =>
      row(
        `${esc(CONTACT_KIND[x.kind] ?? x.kind)}${x.name ? ` · ${esc(x.name)}` : ''}`,
        esc(x.kind === 'EMAIL' ? x.value : phoneText(x.value)),
        'contact',
        x.id,
      ),
    ),
  ].join('');
  const forms = open
    ? `<details class="party-add"><summary class="btn sm">Agregar dirección o contacto</summary>
      <div class="toolbar">${field('Dirección', selectControl({ id: 'pa-kind' }, options(ADDRESS_KIND)))}
        <div class="field grow">${textControl({ label: 'Dirección', id: 'pa-line', maxlength: 300, autocomplete: 'off', placeholder: '6a. calle 3-20, zona 10' })}</div>
        ${field('Departamento', selectControl({ id: 'pa-department' }, options(DEPARTMENT, 'Sin indicar')))}
        <div class="order-buttons"><button type="button" class="btn" data-party-address="${esc(p.id)}">Agregar dirección</button></div></div>
      <div class="toolbar">${field('Contacto', selectControl({ id: 'pk-kind' }, options(CONTACT_KIND)))}
        <div class="field grow">${textControl({ label: 'Número o correo', id: 'pk-value', maxlength: 160, autocomplete: 'off', placeholder: '5555 0199 · nombre@correo.com' })}</div>
        <div class="field">${textControl({ label: p.kind === 'COMPANY' ? 'A quién llega (persona de contacto)' : 'A quién llega (opcional)', id: 'pk-name', maxlength: 160, autocomplete: 'off', placeholder: 'Marta Ajú, contabilidad' })}</div>
        <div class="order-buttons"><button type="button" class="btn" data-party-contact="${esc(p.id)}">Agregar contacto</button></div></div></details>`
    : '';
  return `<section class="card" id="contacts-card"><div class="card-head"><h2>Direcciones y contactos</h2><span class="spacer"></span><span class="muted small">Uno principal por tipo: el que usan los recordatorios.</span></div>
    ${main || others ? `<ul class="party-list">${main}${others}</ul>` : '<p class="muted party-none">Sin direcciones ni contactos registrados.</p>'}${forms}</section>`;
}

/** Its relationships with other customers: family or business, shown on both records. */
function relationshipsCard(v: PartyPage) {
  const p = v.party;
  const open = v.can.update && p.status === 'ACTIVE';
  const list = v.relationships.length
    ? `<ul class="party-list">${v.relationships
        .map(
          (r) =>
            `<li><div><span class="muted small">${esc(ROLE[r.role] ?? r.role)}</span><div>${
              r.other.id
                ? esc(r.other.name ?? '')
                : '<span class="muted">Cliente de otra agente</span>'
            }</div></div><div class="order-buttons">${r.other.id ? `<button type="button" class="btn sm" data-party-open="${esc(r.other.id)}">Abrir ficha</button>` : ''}${open ? `<button type="button" class="btn sm" data-party-unrelate="${esc(r.id)}">Quitar</button>` : ''}</div></li>`,
        )
        .join('')}</ul>`
    : '<p class="muted party-none">Sin relaciones con otros clientes.</p>';
  const form = open
    ? `<details class="party-add"><summary class="btn sm">Registrar una relación</summary>
      <div class="toolbar">${field(`${esc(p.name)} es`, selectControl({ id: 'pr-kind' }, options(RELATIONSHIP)))}
        <div class="field grow">${textControl({ label: 'Buscar el otro cliente', id: 'pr-q', type: 'search', maxlength: 120, autocomplete: 'off', placeholder: 'Nombre, DPI o NIT' })}</div>
        <div class="order-buttons"><button type="button" class="btn" data-party-rel-search>Buscar</button></div></div>
      <div class="toolbar"><div class="field grow" id="pr-other-field">${selectControl({ label: 'Cliente', id: 'pr-other' }, [{ value: '', label: 'Busca primero al otro cliente' }])}</div>
        <div class="order-buttons"><button type="button" class="btn" data-party-relate="${esc(p.id)}">Registrar relación</button></div></div></details>`
    : '';
  return `<section class="card" id="relationships-card"><div class="card-head"><h2>Relaciones</h2><span class="spacer"></span><span class="muted small">Familiares o de empresa; se ven en las dos fichas.</span></div>${list}${form}</section>`;
}

/**
 * Its status: prospecto (no policy in force), vigente, or de baja with the date and reason. It
 * is closed only without policies in force (the API explains it otherwise).
 */
function standingCard(v: PartyPage) {
  const p = v.party;
  if (p.status !== 'ACTIVE') return '';
  const closure = p.closure;
  const head = `<div class="card-head"><h2>Estado del cliente</h2><span class="spacer"></span>${standingPill(p.standing)}</div>`;
  if (closure)
    return `<section class="card" id="standing-card">${head}
      <p class="party-consent-note">De baja desde el ${esc(date(closure.at.slice(0, 10)))} · ${esc(CLOSURE_REASON[closure.reason] ?? closure.reason)}${closure.note ? ` · «${esc(closure.note)}»` : ''} · lo registró ${esc(person(closure.by))}.</p>
      ${v.can.update ? `<div class="form-foot"><span class="muted small">Si vuelve, reactívalo; emitirle una póliza también lo reactiva.</span><button type="button" class="btn" data-party-reopen="${esc(p.id)}">Reactivar cliente</button></div>` : ''}</section>`;
  if (!v.can.update) return '';
  return `<section class="card" id="standing-card">${head}
    <div class="toolbar">${field('Motivo de la baja', selectControl({ id: 'pc-reason' }, options(CLOSURE_REASON)))}
      <div class="field grow">${textControl({ label: 'Detalle (obligatorio si es otro motivo)', id: 'pc-close-note', maxlength: 300, autocomplete: 'off', placeholder: 'Se mudó fuera del país' })}</div>
      <div class="order-buttons"><button type="button" class="btn danger" data-party-close="${esc(p.id)}" aria-describedby="close-consequence">Dar de baja</button></div></div>
    <p class="muted small party-consent-note" id="close-consequence">Un cliente con pólizas vigentes no se da de baja: primero deben cancelarse o vencer. La baja queda con fecha y motivo en su Historia.</p></section>`;
}

async function clientePage(id: string) {
  const v = await api<PartyPage>('GET', `/v1/parties/${encodeURIComponent(id)}`);
  // Its claims, by its policies (#309): only for who follows claims.
  v.claims =
    followsClaims() && v.policies.length
      ? await api<{ items: ClaimRow[] }>(
          'GET',
          `/v1/claims?policyId=${encodeURIComponent(v.policies.map((x) => x.scheduleId).join(','))}`,
        )
          .then((r) => r.items)
          .catch(() => null)
      : followsClaims()
        ? []
        : null;
  const p = v.party;
  openPartyId = p.id;
  const manage = supervises();
  const candidates =
    v.can.merge && p.kind === 'PERSON'
      ? (
          await api<PartyRow[]>(
            'GET',
            `/v1/parties?q=${encodeURIComponent(
              p.name
                .split(' ')
                .filter((w) => w.length > 3)
                .at(-1) ?? p.name,
            )}`,
          ).catch(() => [] as PartyRow[])
        ).filter((x) => x.id !== p.id && x.kind === p.kind)
      : [];
  const openQuotes = v.quotes.filter((q) => !q.binding).length;
  const anonymized = p.status === 'ANONYMIZED';
  const editable = v.can.update && p.status === 'ACTIVE';
  if (!editable) editingParty = false;
  const actions = `${editable && !editingParty ? `<button type="button" class="btn" data-party-edit="${esc(p.id)}">Editar datos</button>` : ''}${v.can.quote && canQuote() && !anonymized ? ` <button type="button" class="btn primary" data-party-quote="${esc(p.id)}">${icon('bolt')}Cotizar para este cliente</button>` : ''}`;
  const ind = v.indicators;
  const dl = (rows: [string, string][]) =>
    `<dl class="party-data">${rows
      .filter(([, v]) => v)
      .map(([k, v]) => `<div><dt>${k}</dt><dd>${v}</dd></div>`)
      .join('')}</dl>`;
  const data = dl([
    ['Tipo', esc(PARTY_KIND[p.kind] ?? p.kind)],
    ['DPI', p.kind === 'PERSON' ? `<span class="mono">${esc(dpiText(p.nationalId))}</span>` : ''],
    ['NIT', p.taxId ? `<span class="mono">${esc(p.taxId)}</span>` : ''],
    ['Nacimiento', p.birthDate ? esc(date(p.birthDate)) : ''],
    ['Contacto', esc(p.contactPerson ?? '')],
    ['Correo', esc(p.email ?? '')],
    ['Teléfono', esc(phoneText(p.phone))],
    ['WhatsApp', esc(phoneText(p.whatsapp ?? null))],
    [
      'Dirección',
      p.address
        ? esc(
            [p.address.line, p.address.department ? DEPARTMENT[p.address.department] : null]
              .filter(Boolean)
              .join(', '),
          )
        : '',
    ],
    ...profileRows(p),
    ['Agente a cargo', esc(cap(p.ownerAgentId))],
    ['Cliente desde', esc(date(p.createdAt.slice(0, 10)))],
    [
      'Registros fusionados',
      v.absorbed
        .map((a) => `${esc(a.name)} <span class="mono muted">${esc(a.id)}</span>`)
        .join('<br>'),
    ],
  ]);
  const consentRows = v.consents.length
    ? `<ol class="timeline consent-log">${v.consents
        .slice(0, 6)
        .map(
          (c) =>
            `<li>${pill(c.granted ? 'ok' : 'bad', `${CONTACT_CHANNEL[c.channel] ?? c.channel}: ${c.granted ? 'autoriza' : 'retira'}`)}<span class="muted small">${esc(when(c.at))} · ${esc(person(c.by))}</span>${c.note ? `<span class="timeline-note">${esc(c.note)}</span>` : ''}</li>`,
        )
        .join('')}</ol>`
    : anonymized
      ? '<p class="muted party-none">Sus datos se borraron a su pedido: la agencia ya no le escribe.</p>'
      : '<p class="muted party-none">Aún no hay consentimientos registrados. Regístralos antes de escribirle por WhatsApp o correo.</p>';
  const consentForm =
    v.can.update && !anonymized
      ? `<div class="toolbar consent-form" id="consent-form">
        ${field(
          'Canal',
          selectControl(
            { id: 'pc-channel' },
            Object.entries(CONTACT_CHANNEL).map(([k, l]) => ({ value: k, label: l })),
          ),
        )}
        ${field(
          'Decisión',
          selectControl({ id: 'pc-granted' }, [
            { value: 'true', label: 'Autoriza' },
            { value: 'false', label: 'No autoriza o retira' },
          ]),
        )}
        <div class="field grow">${textControl({ label: 'Evidencia: cómo lo dijo (obligatoria para autorizar)', id: 'pc-note', maxlength: 300, placeholder: 'Firmó el formulario de Habeas Data el 12 de octubre' })}</div>
        <div class="order-buttons"><button type="button" class="btn" data-party-consent="${esc(p.id)}">Registrar consentimiento</button></div></div>`
      : '';
  const merge =
    v.can.merge && manage && !anonymized
      ? `<section class="card"><div class="card-head"><h2>Fusionar un duplicado</h2><span class="spacer"></span><span class="muted small">El duplicado desaparece; sus pólizas, cotizaciones y consentimientos quedan en este cliente.</span></div>
      <div class="toolbar">${field(
        'Duplicado',
        selectControl(
          { id: 'pm-merged' },
          candidates.length
            ? candidates.map((c) => ({
                value: c.id,
                label: `${c.name} · ${dpiText(c.nationalId)} · ${cap(c.ownerAgentId)}`,
              }))
            : [{ value: '', label: 'No hay clientes con un nombre parecido' }],
        ),
      )}
      <div class="field">${textControl({ label: 'O su número de cliente', id: 'pm-id', class: 'mono', maxlength: 80, placeholder: 'CLI-…' })}</div>
      <div class="order-buttons"><button type="button" class="btn danger" data-party-merge="${esc(p.id)}">Fusionar en ${esc(p.name)}</button></div></div></section>`
      : '';
  const counts: Record<string, number | null> = {
    polizas: v.policies.length,
    siniestros: v.claims ? v.claims.length : null,
    cotizaciones: v.quotes.length,
    prospecto: v.leads.length,
    ordenes: v.workOrders.length,
    expedientes: v.underwriting.files.length,
    tareas: v.openTasks?.length ?? 0,
    notas: v.customerNotes.length,
    documentos: v.documents?.length ?? null,
    comunicaciones: v.communications.length,
    historia: v.timeline.length,
  };
  const tabs = `<div class="lead-filters party-tabs" role="tablist" aria-label="Lo que la agencia tiene del cliente">${PARTY_TABS.filter(
    ([k]) => k !== 'siniestros' || (v.claims !== null && v.claims !== undefined),
  )
    .map(([k, label]) => {
      const n = counts[k] ?? null;
      return `<button type="button" class="btn sm" role="tab" id="pt-${k}" aria-controls="party-tab-panel" aria-selected="${k === partyTab}" aria-pressed="${k === partyTab}" data-party-tab="${k}">${label}${n === null ? '' : ` <span class="muted">${n}</span>`}</button>`;
    })
    .join('')}</div>`;
  return `${pageHead(
    p.name,
    `${standingPill(p.standing)}${p.person?.politicallyExposed ? ` ${pill('warn', `PEP · ${p.person.pepPosition ?? ''}`)}` : ''} ${esc(PARTY_KIND[p.kind] ?? p.kind)} · a cargo de ${esc(cap(p.ownerAgentId))} · <span class="mono">${esc(p.id)}</span>`,
    `<button type="button" class="btn" data-party-back>← Clientes</button> ${actions}`,
  )}
  ${
    v.notes?.length
      ? `<section class="card"><div class="card-head"><h2>Notas para la dirección</h2><span class="spacer"></span><span class="muted small">Solo las ve la dirección: los datos y la historia del cliente no cambiaron.</span></div>
      <ul class="party-list party-none">${v.notes.map((n) => `<li><div>${esc(n.note)}<div class="muted small">${esc(when(n.at))} · agente ${esc(cap(n.agentId))}</div></div></li>`).join('')}</ul></section>`
      : ''
  }
  ${v.mergedFrom ? `<p class="card lead-quoting">${icon('warn')}<span><span class="mono">${esc(v.mergedFrom)}</span> se fusionó en este cliente: lo que tenía está aquí.</span></p>` : ''}
  <section class="kpis party-kpis" aria-label="Resumen del cliente">
    <div class="card kpi"><span class="kpi-label">${icon('policy')}Pólizas vigentes</span><span class="kpi-value">${ind.policiesInForce}</span><span class="kpi-foot">${v.policies.length} ${v.policies.length === 1 ? 'vigencia' : 'vigencias'} en total</span></div>
    <div class="card kpi"><span class="kpi-label">${icon('wallet')}Prima anual</span><span class="kpi-value">${amounts(ind.annualPremium)}</span><span class="kpi-foot">de sus pólizas vigentes</span></div>
    <div class="card kpi ${ind.overdueReceipts ? 'warn' : ''}"><span class="kpi-label">${icon('receipt')}Saldo pendiente</span><span class="kpi-value">${amounts(ind.pending)}</span><span class="kpi-foot">${ind.overdueReceipts ? `${ind.overdueReceipts} ${ind.overdueReceipts === 1 ? 'recibo vencido' : 'recibos vencidos'}: ${v.receivables.totals.map(fmt).join(' · ')}` : 'Sin recibos vencidos'}</span></div>
    ${
      ind.commission12m
        ? `<div class="card kpi"><span class="kpi-label">${icon('settle')}Comisión 12 meses</span><span class="kpi-value">${amounts(ind.commission12m)}</span><span class="kpi-foot">generada por sus pólizas</span></div>`
        : `<div class="card kpi"><span class="kpi-label">${icon('quote')}Cotizaciones abiertas</span><span class="kpi-value">${openQuotes}</span><span class="kpi-foot">${v.quotes.length} en total</span></div>`
    }
    <div class="card kpi"><span class="kpi-label">${icon('client')}Cliente desde</span><span class="kpi-value kpi-date">${esc(date(ind.customerSince.slice(0, 10)))}</span><span class="kpi-foot">${esc(STANDING[p.standing ?? '']?.[0] ?? '')}</span></div>
  </section>
  <div class="party-grid">
    ${
      editingParty
        ? partyEditForm(v)
        : `<section class="card" id="party-data-card"><div class="card-head"><h2>Datos</h2><span class="spacer"></span>${p.nationalIdMasked ? '<span class="muted small">El DPI completo lo ven el agente a cargo y la administración.</span>' : ''}</div>${data}
      ${p.sensitiveHidden ? `<p class="muted small party-consent-note">${icon('lock')} PEP, origen de los fondos e ingreso los ven la agente a cargo, la dirección y la administración.</p>` : ''}</section>`
    }
    <section class="card" id="consent-card"><div class="card-head"><h2>Consentimiento (Habeas Data)</h2></div>
      <p class="muted small party-consent-note">Es el único registro de la agencia: los recordatorios de pago y de renovación solo salen por los canales que el cliente autoriza aquí, y dejan de salir en cuanto lo retira.</p>
      <div class="consent-pills party-consent">${p.consent.map(consentPill).join('')}</div>
      ${consentRows}${consentForm}</section>
  </div>
  <div class="party-grid">${contactsCard(v)}${relationshipsCard(v)}</div>
  <section class="card party-tabs-card">${tabs}<div id="party-tab-panel" role="tabpanel" aria-labelledby="pt-${esc(partyTab)}">${partyTabBody(v)}</div></section>
  ${standingCard(v)}${merge}${erasureCard(v)}`;
}

/**
 * Habeas Data: the customer's request to erase their data. The agent in charge or the
 * administration records it with how the customer asked; the direction approves it, and the
 * data is anonymized right then (policies, receipts and accounting stay, with a reference).
 */
function erasureCard(v: PartyPage) {
  const p = v.party;
  const e = p.erasure ?? null;
  const head = `<div class="card-head">${icon('lock')}<h2>Borrar sus datos (Habeas Data)</h2></div>`;
  if (p.status === 'ANONYMIZED' && e?.done)
    return `<section class="card" id="erasure-card">${head}
      <p class="uw-ready">${icon('ok')}<span>Datos anonimizados el ${esc(when(e.done.at))} a pedido del cliente. Sus pólizas, recibos y asientos contables se conservan con la referencia <b class="mono">${esc(e.done.reference)}</b>.</span></p>
      <p class="muted small party-consent-note">Lo pidió ante ${esc(person(e.requested.by))} el ${esc(when(e.requested.at))}${e.approved ? `; lo aprobó ${esc(person(e.approved.by))} el ${esc(when(e.approved.at))}` : ''}.</p></section>`;
  if (e && !e.approved)
    return `<section class="card" id="erasure-card">${head}
      <p class="note">${icon('warn')}<span>El cliente pidió borrar sus datos: lo anotó ${esc(person(e.requested.by))} el ${esc(when(e.requested.at))}${e.requested.note ? ` · «${esc(e.requested.note)}»` : ''}. Falta la aprobación de la dirección.</span></p>
      ${
        v.can.approveErasure
          ? `<div class="form-foot"><span class="muted small" id="erasure-consequence">Al aprobar se borran ahora su nombre, DPI, NIT y contacto de la ficha, sus pólizas, cotizaciones, prospectos, órdenes y expedientes. No se puede deshacer.</span>
            <button type="button" class="btn danger" data-party-erase="${esc(p.id)}" aria-describedby="erasure-consequence">Aprobar y anonimizar</button></div>`
          : '<p class="muted small">La dirección lo aprueba desde esta ficha.</p>'
      }</section>`;
  if (!e && v.can.requestErasure && p.status !== 'MERGED')
    return `<section class="card" id="erasure-card">${head}
      <p class="muted small party-consent-note">Si el cliente pide que la agencia borre sus datos, anótalo aquí con cómo lo pidió. La dirección lo aprueba; hasta entonces no se borra nada.</p>
      <div class="toolbar"><div class="field grow">${textControl({ label: 'Cómo lo pidió (obligatorio)', id: 'pe-note', maxlength: 300, placeholder: 'Carta firmada entregada en la agencia el 12 de octubre' })}</div>
      <div class="order-buttons"><button type="button" class="btn" data-party-erasure-request="${esc(p.id)}">Anotar pedido de borrado</button></div></div></section>`;
  return '';
}

/** From a policy or a quote: its policyholder's file, made a customer now if the issuance could not. */
const openPartyOf: ClickAction = (t, d) => {
  if (!(d.partyOfSchedule || d.partyOfQuote)) return;
  const toConsent = d.consent !== undefined;
  void act(t, 'Buscando al cliente…', async () => {
    // From a policy: its policyholder is made a customer now if the issuance could not.
    const r = d.partyOfSchedule
      ? await api<{ partyId: string; name: string }>(
          'POST',
          `/v1/schedules/${encodeURIComponent(d.partyOfSchedule)}/party`,
        )
      : await api<{ partyId: string; name: string }>(
          'GET',
          `/v1/parties/lookup?quoteId=${encodeURIComponent(d.partyOfQuote ?? '')}`,
        );
    openPartyId = r.partyId;
    partyTab = 'polizas';
    await render('clientes');
    if (toConsent) {
      $('#consent-card')?.scrollIntoView({ block: 'start' });
      $<HTMLSelectElement>('#pc-channel')?.focus();
      return `Registra el consentimiento de ${r.name} en su ficha: los recordatorios usan lo que diga aquí.`;
    }
    return `Ficha de ${r.name}.`;
  });
};

/** The value of a control of the page, trimmed ('' when it is not there). */
const valueOf = (sel: string) => ($(sel) as HTMLInputElement | null)?.value.trim() ?? '';
/** A record command that answers the customer's whole view: re-paint it quietly. */
const recordChange = (
  t: HTMLElement,
  busy: string,
  run: () => Promise<unknown>,
  done: string | (() => string),
) =>
  void act(t, busy, async () => {
    await run();
    await render('clientes', { quiet: true });
    return typeof done === 'string' ? done : done();
  });
const partyPath = (id: string, rest = '') => `/v1/parties/${encodeURIComponent(id)}${rest}`;

/** What the edit form says, as the API takes it (absent fields stay as they are). */
function editedParty(form: HTMLElement) {
  const id = form.dataset.party ?? '';
  const kind = $('#pf-trade') || $('#pf-rep-name') ? 'COMPANY' : 'PERSON';
  const name = textInput('#pf-name', 'el nombre del cliente');
  const cui = valueOf('#pf-cui').replace(/[\s-]/g, '');
  const nit = valueOf('#pf-nit').replace(/\s/g, '').toUpperCase();
  if ($('#pf-cui') && !/^\d{13}$/.test(cui)) throw new InputError('El DPI (CUI) lleva 13 dígitos.');
  if ((kind === 'COMPANY' || nit) && !/^\d{1,12}-[\dK]$/.test(nit))
    throw new InputError(
      'Escribe el NIT con guion antes del último dígito, por ejemplo 1234567-8.',
    );
  const line = valueOf('#pf-address');
  const department = valueOf('#pf-department');
  const or = (v: string) => v || null;
  const pep = ($('#pf-pep') as HTMLInputElement | null)?.checked === true;
  if (pep && !valueOf('#pf-pep-position'))
    throw new InputError('Escribe el cargo público de la persona expuesta políticamente.');
  const employees = valueOf('#pf-employees');
  return {
    id,
    body: {
      kind,
      name,
      ...($('#pf-cui') ? { nationalId: cui } : {}),
      ...(nit ? { taxId: nit } : {}),
      ...(valueOf('#pf-birth') ? { birthDate: valueOf('#pf-birth') } : {}),
      ...(valueOf('#pf-email') ? { email: valueOf('#pf-email') } : {}),
      ...(valueOf('#pf-phone') ? { phone: valueOf('#pf-phone') } : {}),
      whatsapp: or(valueOf('#pf-whatsapp')),
      ...(line || department ? { address: { line, department: department || null } } : {}),
      ...(valueOf('#pf-contact') ? { contactPerson: valueOf('#pf-contact') } : {}),
      ...(kind === 'PERSON'
        ? {
            // Who may not read the sensitive part does not see it, so it is not sent (it stays).
            ...($('#pf-funds')
              ? {
                  person: {
                    sex: or(valueOf('#pf-sex')),
                    maritalStatus: or(valueOf('#pf-marital')),
                    occupation: or(valueOf('#pf-occupation')),
                    workplace: or(valueOf('#pf-workplace')),
                    nationality: or(valueOf('#pf-nationality')),
                    politicallyExposed: pep,
                    pepPosition: pep ? valueOf('#pf-pep-position') : null,
                    fundsSource: or(valueOf('#pf-funds')),
                    monthlyIncome: or(valueOf('#pf-income')),
                  },
                }
              : {}),
          }
        : {
            company: {
              tradeName: or(valueOf('#pf-trade')),
              economicActivity: or(valueOf('#pf-activity')),
              incorporatedOn: or(valueOf('#pf-incorporated')),
              legalRepresentative: valueOf('#pf-rep-name')
                ? {
                    name: valueOf('#pf-rep-name'),
                    nationalId: or(valueOf('#pf-rep-dpi').replace(/[\s-]/g, '')),
                    position: or(valueOf('#pf-rep-position')),
                  }
                : null,
              employees: employees ? Number(employees) : null,
            },
          }),
    },
  };
}

/** The customer record's own buttons and forms (#312). */
const recordActions: Actions = {
  click: {
    partyEdit: () => {
      editingParty = true;
      void render('clientes', { quiet: true }).then(() => $('#pf-name')?.focus());
    },
    partyEditCancel: () => {
      editingParty = false;
      void render('clientes', { quiet: true }).then(() => $('[data-party-edit]')?.focus());
    },
    partyAddress: (t, d) => {
      const id = d.partyAddress ?? '';
      const line = valueOf('#pa-line');
      recordChange(
        t,
        'Agregando la dirección…',
        async () => {
          if (!line) throw new InputError('Escribe la dirección.');
          await api('POST', partyPath(id, '/addresses'), {
            kind: valueOf('#pa-kind'),
            line,
            department: valueOf('#pa-department') || null,
          });
        },
        'Dirección agregada.',
      );
    },
    partyContact: (t, d) => {
      const id = d.partyContact ?? '';
      const kind = valueOf('#pk-kind');
      recordChange(
        t,
        'Agregando el contacto…',
        async () => {
          if (!valueOf('#pk-value')) throw new InputError('Escribe el número o el correo.');
          await api('POST', partyPath(id, '/contacts'), {
            kind,
            value: valueOf('#pk-value'),
            ...(valueOf('#pk-name') ? { name: valueOf('#pk-name') } : {}),
          });
        },
        `${CONTACT_KIND[kind] ?? 'Contacto'} agregado.`,
      );
    },
    partyMain: (t, d) => {
      const [kind, id] = (d.partyMain ?? '').split(':');
      recordChange(
        t,
        'Cambiando el principal…',
        () =>
          api(
            'POST',
            partyPath(
              openPartyId ?? '',
              `/${kind === 'address' ? 'addresses' : 'contacts'}/${encodeURIComponent(id ?? '')}/main`,
            ),
          ),
        kind === 'address' ? 'Es la dirección principal.' : 'Es el contacto principal de su tipo.',
      );
    },
    partyRemove: (t, d) => {
      const [kind, id] = (d.partyRemove ?? '').split(':');
      recordChange(
        t,
        'Quitando…',
        () =>
          api(
            'DELETE',
            partyPath(
              openPartyId ?? '',
              `/${kind === 'address' ? 'addresses' : 'contacts'}/${encodeURIComponent(id ?? '')}`,
            ),
          ),
        kind === 'address' ? 'Dirección quitada.' : 'Contacto quitado.',
      );
    },
    partyRelSearch: (t) => {
      const q = valueOf('#pr-q');
      void act(t, 'Buscando…', async () => {
        if (!q) throw new InputError('Escribe el nombre, el DPI o el NIT del otro cliente.');
        const found = (
          await api<PartyRow[]>('GET', `/v1/parties?q=${encodeURIComponent(q)}`)
        ).filter((x) => x.id !== openPartyId);
        const holder = $('#pr-other-field');
        if (holder)
          holder.innerHTML = selectControl(
            { label: 'Cliente', id: 'pr-other' },
            found.length
              ? found.map((x) => ({
                  value: x.id,
                  label: `${x.name} · ${x.kind === 'PERSON' ? dpiText(x.nationalId) : `NIT ${x.taxId ?? ''}`}`,
                }))
              : [{ value: '', label: `Ningún cliente coincide con «${q}»` }],
          );
        return found.length
          ? `${found.length} ${found.length === 1 ? 'cliente encontrado' : 'clientes encontrados'}: elige uno.`
          : 'Ningún cliente coincide.';
      });
    },
    partyRelate: (t, d) => {
      const id = d.partyRelate ?? '';
      const kind = valueOf('#pr-kind');
      const relatedId = valueOf('#pr-other');
      recordChange(
        t,
        'Registrando la relación…',
        async () => {
          if (!relatedId) throw new InputError('Busca y elige al otro cliente.');
          await api('POST', partyPath(id, '/relationships'), { relatedId, kind });
        },
        'Relación registrada: se ve en las dos fichas.',
      );
    },
    partyUnrelate: (t, d) =>
      recordChange(
        t,
        'Quitando la relación…',
        () =>
          api(
            'DELETE',
            partyPath(
              openPartyId ?? '',
              `/relationships/${encodeURIComponent(d.partyUnrelate ?? '')}`,
            ),
          ),
        'Relación quitada.',
      ),
    partyClose: (t, d) => {
      const id = d.partyClose ?? '';
      const reason = valueOf('#pc-reason');
      const note = valueOf('#pc-close-note');
      recordChange(
        t,
        'Dando de baja…',
        async () => {
          if (reason === 'OTHER' && !note) throw new InputError('Escribe el motivo de la baja.');
          await api('POST', partyPath(id, '/closure'), { reason, ...(note ? { note } : {}) });
        },
        `Cliente dado de baja: ${CLOSURE_REASON[reason] ?? reason}.`,
      );
    },
    partyReopen: (t, d) =>
      recordChange(
        t,
        'Reactivando…',
        () => api('POST', partyPath(d.partyReopen ?? '', '/reopen')),
        'Cliente reactivado.',
      ),
    noteAdd: (t, d) => {
      const id = d.noteAdd ?? '';
      const body = valueOf('#pn-body');
      const pinned = ($('#pn-pinned') as HTMLInputElement | null)?.checked === true;
      recordChange(
        t,
        'Guardando la nota…',
        async () => {
          if (!body) throw new InputError('Escribe la nota.');
          await api('POST', partyPath(id, '/notes'), { body, pinned });
        },
        pinned ? 'Nota agregada y fijada.' : 'Nota agregada.',
      );
    },
    notePin: (t, d) =>
      recordChange(
        t,
        'Guardando…',
        () =>
          api(
            'POST',
            partyPath(openPartyId ?? '', `/notes/${encodeURIComponent(d.notePin ?? '')}/pin`),
            {
              pinned: d.pinned === 'true',
            },
          ),
        d.pinned === 'true' ? 'Nota fijada.' : 'Nota desfijada.',
      ),
    noteEdit: (_, d) => {
      editingNote = d.noteEdit ?? null;
      void render('clientes', { quiet: true }).then(() => $('#pn-fix')?.focus());
    },
    noteCancel: () => {
      editingNote = null;
      void render('clientes', { quiet: true });
    },
    noteSave: (t, d) => {
      const body = valueOf('#pn-fix');
      recordChange(
        t,
        'Corrigiendo la nota…',
        async () => {
          if (!body) throw new InputError('Escribe la nota.');
          await api(
            'PUT',
            partyPath(openPartyId ?? '', `/notes/${encodeURIComponent(d.noteSave ?? '')}`),
            {
              body,
            },
          );
          editingNote = null;
        },
        'Nota corregida.',
      );
    },
    partyUpload: (t, d) => {
      const id = d.partyUpload ?? '';
      const kind = valueOf('#pd-kind');
      const file = ($('#pd-file') as HTMLInputElement | null)?.files?.[0];
      recordChange(
        t,
        'Subiendo el documento…',
        async () => {
          if (!file) throw new InputError('Elige el archivo del documento.', 'pd-file');
          await sendFile(partyPath(id, `/documents/${encodeURIComponent(kind)}`), file);
        },
        () => `${DOCUMENT_KIND[kind] ?? 'Documento'} guardado.`,
      );
    },
    partyDoc: (t, d) =>
      void act(t, 'Abriendo el documento…', async () => {
        const name = await download(
          partyPath(
            openPartyId ?? '',
            `/documents/${encodeURIComponent(d.partyDoc ?? '')}/content`,
          ),
        );
        return `«${name}» descargado; queda registrado quién lo abrió.`;
      }),
    partyExport: (t) =>
      void act(t, 'Preparando el Excel…', async () => {
        const query = portfolioQuery();
        const name = await download(
          `/v1/parties/export?format=xlsx${query ? `&${query}` : ''}`,
          'cartera-de-clientes.xlsx',
        );
        return `«${name}» descargado.`;
      }),
  },
  submit: {
    'party-edit-form': (ev) => {
      ev.preventDefault();
      const form = ev.target as HTMLElement;
      void act($('#party-edit-submit'), 'Guardando los cambios…', async () => {
        const { id, body } = editedParty(form);
        const p = await api<PartyRow>('PUT', partyPath(id), body);
        editingParty = false;
        await render('clientes', { quiet: true });
        $('[data-party-edit]')?.focus();
        return `Datos de ${p.name} guardados; el cambio queda en su Historia.`;
      });
    },
  },
  change: {
    'party-standing': (c) => {
      partyStanding = c.value;
      delete listPages.clientes;
      void render('clientes', { quiet: true }).then(() => $('#party-standing')?.focus());
    },
    'party-agent': (c) => {
      partyAgent = c.value;
      delete listPages.clientes;
      void render('clientes', { quiet: true }).then(() => $('#party-agent')?.focus());
    },
  },
};

/** What this view answers: its buttons (by data attribute), forms (by id) and controls (by id). */
export const actions: Actions = {
  click: {
    ...recordActions.click,
    partyOpen: (_, d) => {
      if (!d.partyOpen) return;
      openPartyId = d.partyOpen;
      partyTab = 'polizas';
      editingParty = false;
      editingNote = null;
      void render('clientes');
    },
    partyBack: () => {
      openPartyId = null;
      void render('clientes');
    },
    partyClear: () => {
      partyQuery = '';
      partyStanding = '';
      partyAgent = '';
      delete listPages.clientes;
      void render('clientes', { quiet: true });
    },
    partyTab: (_, d) => {
      if (!d.partyTab) return;
      partyTab = d.partyTab;
      void render('clientes', { quiet: true }).then(() => $(`#pt-${partyTab}`)?.focus());
    },
    partyOfSchedule: openPartyOf,
    partyOfQuote: openPartyOf,
    partyQuote: (t, d) => {
      if (!d.partyQuote) return;
      const partyId = d.partyQuote;
      void act(t, 'Preparando la cotización del cliente…', async () => {
        const v = await api<PartyPage>('GET', `/v1/parties/${encodeURIComponent(partyId)}`);
        const p = v.party;
        setQuoting({
          quoteParty: { id: p.id, name: p.name },
          quoteLead: null,
          holderDraft: null,
          // The holder form of «Emitir» starts with the customer's data (a masked DPI is left out).
          quoteHolder: {
            fullName: p.name,
            ...(p.nationalId && !p.nationalIdMasked ? { nationalId: p.nationalId } : {}),
            ...(p.taxId ? { taxId: p.taxId } : {}),
            ...(p.email ? { email: p.email } : {}),
            ...(p.phone ? { phone: p.phone } : {}),
          },
          lastQuote: null,
          quoteDraft: null,
          fieldErrors: {},
        });
        await render('cotizar');
        return `Cotizando para ${p.name}.`;
      });
    },
    partyConsent: (t, d) => {
      if (!d.partyConsent) return;
      const partyId = d.partyConsent;
      void act(t, 'Registrando el consentimiento…', async () => {
        const channel = ($('#pc-channel') as HTMLSelectElement).value;
        const granted = ($('#pc-granted') as HTMLSelectElement).value === 'true';
        const note = ($('#pc-note') as HTMLInputElement).value.trim();
        if (granted && !note)
          throw new InputError('Escribe la evidencia: cómo autorizó el cliente ese canal.');
        await api('POST', `/v1/parties/${encodeURIComponent(partyId)}/consents`, {
          channel,
          granted,
          ...(note ? { note } : {}),
        });
        await render('clientes', { quiet: true });
        return `${CONTACT_CHANNEL[channel] ?? channel}: ${granted ? 'autoriza' : 'no autoriza'}, registrado con fecha y hora.`;
      });
    },
    partyErasureRequest: (t, d) => {
      if (!d.partyErasureRequest) return;
      const partyId = d.partyErasureRequest;
      void act(t, 'Anotando el pedido…', async () => {
        const note = ($('#pe-note') as HTMLInputElement).value.trim();
        if (!note) throw new InputError('Escribe cómo pidió el cliente que se borren sus datos.');
        await api('POST', `/v1/parties/${encodeURIComponent(partyId)}/erasure-request`, { note });
        await render('clientes', { quiet: true });
        return 'Pedido de borrado anotado: la dirección lo aprueba desde la ficha.';
      });
    },
    partyErase: (t, d) => {
      if (!d.partyErase) return;
      const partyId = d.partyErase;
      void act(t, 'Anonimizando…', async () => {
        const v = await api<PartyPage>(
          'POST',
          `/v1/parties/${encodeURIComponent(partyId)}/erasure-approval`,
          {},
        );
        await render('clientes', { quiet: true });
        return `Datos del cliente anonimizados: queda como ${v.party.erasure?.done?.reference ?? v.party.name}.`;
      });
    },
    partyMerge: (t, d) => {
      if (!d.partyMerge) return;
      const survivor = d.partyMerge;
      void act(t, 'Fusionando…', async () => {
        const typed = ($('#pm-id') as HTMLInputElement).value.trim();
        const mergedId = typed || ($('#pm-merged') as HTMLSelectElement).value;
        if (!mergedId) throw new InputError('Elige el duplicado o escribe su número de cliente.');
        const v = await api<PartyPage>(
          'POST',
          `/v1/parties/${encodeURIComponent(survivor)}/merge`,
          {
            mergedId,
          },
        );
        await render('clientes', { quiet: true });
        return `${mergedId} fusionado: ${v.party.name} conserva sus pólizas, cotizaciones y consentimientos.`;
      });
    },
  },
  change: recordActions.change ?? {},
  submit: {
    ...recordActions.submit,
    'party-search-form': (ev) => {
      ev.preventDefault();
      partyQuery = ($('#party-q') as HTMLInputElement).value.trim();
      delete listPages.clientes;
      void render('clientes', { quiet: true }).then(() => $('#party-q')?.focus());
    },
    'party-form': (ev) => {
      ev.preventDefault();
      void act($('#party-submit'), 'Registrando al cliente…', async () => {
        const value = (sel: string) => ($(sel) as HTMLInputElement | null)?.value.trim() ?? '';
        const kind = value('#p-kind');
        const cui = value('#p-cui').replace(/[\s-]/g, '');
        const nit = value('#p-nit').replace(/\s/g, '').toUpperCase();
        if (kind === 'PERSON' && !/^\d{13}$/.test(cui))
          throw new InputError('El DPI (CUI) lleva 13 dígitos.');
        if ((kind === 'COMPANY' || nit) && !/^\d{1,12}-[\dK]$/.test(nit))
          throw new InputError(
            'Escribe el NIT con guion antes del último dígito, por ejemplo 1234567-8.',
          );
        const line = value('#p-address');
        const department = value('#p-department');
        const p = await api<PartyRow>('POST', '/v1/parties', {
          kind,
          name: textInput('#p-name', 'el nombre del cliente'),
          ...(kind === 'PERSON' ? { nationalId: cui } : {}),
          ...(nit ? { taxId: nit } : {}),
          ...(value('#p-birth') && kind === 'PERSON' ? { birthDate: value('#p-birth') } : {}),
          ...(value('#p-email') ? { email: value('#p-email') } : {}),
          ...(value('#p-phone') ? { phone: value('#p-phone') } : {}),
          ...(value('#p-contact') && kind === 'COMPANY'
            ? { contactPerson: value('#p-contact') }
            : {}),
          ...(line || department ? { address: { line, department: department || null } } : {}),
          ...(value('#p-agent') ? { agentId: value('#p-agent') } : {}),
        });
        openPartyId = p.id;
        partyTab = 'polizas';
        await render('clientes');
        return `${p.name} registrado como cliente.`;
      });
    },
  },
};
